import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Share2, Mail, MessageSquareText, Linkedin, Twitter, Facebook,
  MessageCircle, Slack, Link as LinkIcon, X, Smartphone, TrendingUp,
} from "lucide-react";

/**
 * Share-anywhere popover.
 *
 * Renders a "Share" button that opens a popover with the most common channels:
 * Copy link · Email · SMS · LinkedIn · X (Twitter) · Facebook · WhatsApp ·
 * Slack · Native (Web Share API on mobile).
 *
 * Intent URLs all use standard scheme- or web-share endpoints so nothing
 * server-side is required. The URL passed in is what gets copied / shared.
 *
 * Props:
 *   url: canonical, public, sharable URL
 *   title: short title (fills email subject, X tweet text, etc.)
 *   message: longer description (email body, SMS body, etc.)
 *   testIdPrefix: data-testid prefix for child buttons
 *   align: "left" | "right" — popover anchor side
 *   topChannel: optional `{channel, conversion_rate_pct, conversions, hits}`
 *               from /admin/share-events/summary. When supplied, the popover
 *               surfaces a "best converter" callout that highlights the
 *               historically highest-converting channel + bumps it visually
 *               so admins close the loop instead of guessing.
 */
export default function ShareMenu({
  url,
  title = "Nodera Health · cash-pay operating layer",
  message = "Watch a 7-minute walkthrough of how Nodera Health builds the operating layer for cash-pay clinics.",
  testIdPrefix = "share",
  align = "right",
  topChannel = null,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const _withUtm = (channel) => {
    if (!url) return "";
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}utm_source=${encodeURIComponent(channel)}&utm_medium=share&utm_campaign=demo_video`;
  };

  // Per-channel share targets — each url carries its own UTM tag so the
  // marketing /demo page (which logs hits) can attribute traffic.
  const linkUrl = _withUtm("direct"); // copy / native share fall back to this
  const u = (chan) => encodeURIComponent(_withUtm(chan));
  const t = encodeURIComponent(title || "");
  const m = encodeURIComponent(message || "");
  const both = (chan) => encodeURIComponent(`${title}\n\n${message}\n${_withUtm(chan)}`);

  const channels = [
    {
      key: "copy",
      label: "Copy link",
      icon: LinkIcon,
      tone: "slate",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(linkUrl);
          toast.success("Link copied");
        } catch {
          toast.error("Could not access clipboard");
        }
      },
    },
    typeof navigator !== "undefined" && navigator.share
      ? {
          key: "native",
          label: "Phone share…",
          icon: Smartphone,
          tone: "slate",
          onClick: async () => {
            try {
              await navigator.share({ title, text: message, url: linkUrl });
            } catch {
              /* user dismissed */
            }
          },
        }
      : null,
    {
      key: "email",
      label: "Email",
      icon: Mail,
      tone: "slate",
      href: `mailto:?subject=${t}&body=${both("email")}`,
    },
    {
      key: "sms",
      label: "SMS / iMessage",
      icon: MessageSquareText,
      tone: "slate",
      href: `sms:?&body=${both("sms")}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      tone: "blue",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u("linkedin")}`,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      icon: Twitter,
      tone: "slate",
      href: `https://twitter.com/intent/tweet?text=${t}&url=${u("twitter")}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      tone: "blue",
      href: `https://www.facebook.com/sharer/sharer.php?u=${u("facebook")}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      tone: "emerald",
      href: `https://wa.me/?text=${both("whatsapp")}`,
    },
    {
      key: "slack",
      label: "Slack (copy + paste)",
      icon: Slack,
      tone: "violet",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(`${title}\n${message}\n${_withUtm("slack")}`);
          toast.success("Copied — paste into Slack");
        } catch {
          toast.error("Could not access clipboard");
        }
      },
    },
  ].filter(Boolean);

  const TONE = {
    slate: "text-slate-700 hover:bg-slate-100",
    blue: "text-blue-700 hover:bg-blue-50",
    emerald: "text-emerald-700 hover:bg-emerald-50",
    violet: "text-violet-700 hover:bg-violet-50",
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={!url}
        data-testid={`${testIdPrefix}-toggle`}
        className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
      >
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      {open && (
        <div
          className={`absolute z-30 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden ${
            align === "right" ? "right-0" : "left-0"
          }`}
          data-testid={`${testIdPrefix}-popover`}
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Share via
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 -mr-1 rounded hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
          {topChannel && (
            <div
              className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 flex items-start gap-2"
              data-testid={`${testIdPrefix}-top-channel`}
            >
              <TrendingUp className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-[11px] leading-tight text-emerald-900">
                <span className="font-bold capitalize">{topChannel.channel}</span>{" "}
                converts best —{" "}
                <span className="font-semibold tabular-nums">
                  {topChannel.conversion_rate_pct}%
                </span>{" "}
                of share-tagged hits become demo requests
                {topChannel.conversions ? (
                  <span className="text-emerald-700/80">
                    {" "}({topChannel.conversions}/{topChannel.hits})
                  </span>
                ) : null}
                .
              </div>
            </div>
          )}
          <div className="p-1.5 grid grid-cols-1">
            {channels.map((c) => {
              const Icon = c.icon;
              const isWinner = topChannel && topChannel.channel === c.key;
              const cls = `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${TONE[c.tone]} cursor-pointer ${
                isWinner ? "ring-1 ring-emerald-300 bg-emerald-50/40" : ""
              }`;
              const winnerBadge = isWinner ? (
                <span
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5"
                  data-testid={`${testIdPrefix}-${c.key}-winner-tag`}
                >
                  <TrendingUp className="w-2.5 h-2.5" /> top
                </span>
              ) : null;
              if (c.href) {
                return (
                  <a
                    key={c.key}
                    href={c.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setOpen(false)}
                    data-testid={`${testIdPrefix}-${c.key}`}
                    className={cls}
                  >
                    <Icon className="w-4 h-4" />
                    {c.label}
                    {winnerBadge}
                  </a>
                );
              }
              return (
                <button
                  key={c.key}
                  onClick={() => {
                    c.onClick?.();
                    setOpen(false);
                  }}
                  data-testid={`${testIdPrefix}-${c.key}`}
                  className={`${cls} text-left`}
                >
                  <Icon className="w-4 h-4" />
                  {c.label}
                  {winnerBadge}
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-500 break-all">
            {linkUrl}
          </div>
        </div>
      )}
    </div>
  );
}
